/**
 * Patient Action Functions - Fetch and mutation operations for Patients
 *
 * These functions handle all patient fetch and mutation operations.
 * Uses validated fetch with Zod schemas for runtime type safety.
 */

import { PatientSchema, type Patient, type CreatePatientBody, type UpdatePatientBody } from "@/isomorphic/schemas";
import { getAtomStore } from "@lib/atoms";
import { API_ROUTES } from "@/frontend/lib/api/routes";
import { fetchValidated, fetchValidatedArray, postValidated, putValidated } from "@/frontend/lib/validation";
import {
  _apiWorkspacePatientsAtom,
  workspacePatientsStatusAtom,
  workspacePatientsErrorAtom,
  currentPatientAtom,
  currentPatientStatusAtom,
} from "../core/patients";

// ============================================================================
// FETCH FUNCTIONS
// ============================================================================

/**
 * Fetch all patients for a workspace
 */
export const fetchWorkspacePatients = async (jwt: string, workspaceId: string): Promise<void> => {
  const store = getAtomStore();

  if (!jwt || !workspaceId) {
    return;
  }

  store.set(workspacePatientsStatusAtom, "loading");
  store.set(workspacePatientsErrorAtom, null);

  const result = await fetchValidatedArray(PatientSchema, API_ROUTES.patients.getByWorkspace(workspaceId), {
    jwt,
    context: "Workspace Patients",
  });

  if (result.success) {
    store.set(_apiWorkspacePatientsAtom, result.data);
    store.set(workspacePatientsStatusAtom, "success");
  } else {
    console.error("[PatientActions] Error fetching workspace patients:", result.error);
    store.set(workspacePatientsStatusAtom, "error");
    store.set(workspacePatientsErrorAtom, result.error);
  }
};

/**
 * Fetch a single patient by ID
 */
export const fetchPatient = async (jwt: string, patientId: string): Promise<Patient | null> => {
  const store = getAtomStore();

  if (!jwt || !patientId) {
    return null;
  }

  store.set(currentPatientStatusAtom, "loading");

  const result = await fetchValidated(PatientSchema, API_ROUTES.patients.getById(patientId), {
    jwt,
    context: `Patient ${patientId}`,
  });

  if (result.success) {
    store.set(currentPatientAtom, result.data);
    store.set(currentPatientStatusAtom, "success");
    return result.data;
  } else {
    console.error("[PatientActions] Error fetching patient:", result.error);
    store.set(currentPatientStatusAtom, "error");
    return null;
  }
};

// ============================================================================
// MUTATION FUNCTIONS
// ============================================================================

/**
 * Create a new patient
 */
export const createPatient = async (jwt: string, payload: CreatePatientBody): Promise<Patient | null> => {
  const store = getAtomStore();

  if (!jwt) {
    return null;
  }

  const result = await postValidated(PatientSchema, API_ROUTES.patients.create, payload, {
    jwt,
    context: "Create Patient",
  });

  if (result.success) {
    // Update local state
    const currentPatients = store.get(_apiWorkspacePatientsAtom);
    store.set(_apiWorkspacePatientsAtom, [...currentPatients, result.data]);
    return result.data;
  } else {
    console.error("[PatientActions] Error creating patient:", result.error);
    throw new Error(result.error);
  }
};

/**
 * Update an existing patient
 */
export const updatePatient = async (
  jwt: string,
  patientId: string,
  payload: Partial<UpdatePatientBody>,
): Promise<Patient | null> => {
  const store = getAtomStore();

  if (!jwt || !patientId) {
    return null;
  }

  const result = await putValidated(PatientSchema, API_ROUTES.patients.update(patientId), payload, {
    jwt,
    context: `Update Patient ${patientId}`,
  });

  if (result.success) {
    // Update local state
    const currentPatients = store.get(_apiWorkspacePatientsAtom);
    store.set(
      _apiWorkspacePatientsAtom,
      currentPatients.map((p) => (p.id === patientId ? result.data : p)),
    );

    // Update current patient if it's the one being edited
    const currentPatient = store.get(currentPatientAtom);
    if (currentPatient?.id === patientId) {
      store.set(currentPatientAtom, result.data);
    }

    return result.data;
  } else {
    console.error("[PatientActions] Error updating patient:", result.error);
    throw new Error(result.error);
  }
};
